import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconDownload, IconShare3 } from "@tabler/icons-react";
import PDFPreview from "@/pages/documents/PDFPreview";
import { MButton } from "@/components/ui/atoms/MButtons";
import MLoader from "@/components/ui/atoms/MLoader";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getMemberInfo } from "@/hooks/useMembers";
import { useIsDesktopPWA } from "@/hooks/useIsDesktopPWA";
import { usePillbox } from "@/hooks/usePillbox";
import {
  triggerNativePillboxShare,
  triggerNativePillboxShoppingListShare,
} from "@/utils/nativeBridge";

/**
 * Shows a Pillbox PDF on screen before it can be shared.
 *
 * Previously "Share" handed straight off to the OS share sheet, so the member
 * had no way to check what they were about to send. Layout deliberately
 * mirrors HealthSummaryViewer — same centred title, same PDFPreview pager,
 * same full-width Share footer — so the two viewers read as one pattern.
 * Back is supplied by NoFooterLayout, which reads `backUrl` off route state.
 */
export type PillboxPdfKind = "pillbox" | "shopping-list";

export interface PillboxPdfViewerState {
  kind: PillboxPdfKind;
  /** Which Pillbox tab the PDF represents — "pillbox" kind only. */
  view?: "list" | "schedule";
  /** Selected day for the schedule view — "pillbox" kind only. */
  date?: string;
  /** Where Back returns to — consumed by NoFooterLayout, not this component. */
  backUrl?: string;
}

const PillboxPdfViewer: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { showToast } = useToast();
  const isDesktop = useIsDesktopPWA();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName || "";
  const { downloadSharePdf, downloadShoppingListPdf } = usePillbox(memberId);

  const state = (location.state as PillboxPdfViewerState | null) ?? {
    kind: "pillbox",
  };
  const { kind, view = "schedule", date } = state;
  const isShoppingList = kind === "shopping-list";

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [, setNoOfPages] = useState<number>(0);

  const errorKey = isShoppingList
    ? "pillbox.shoppingList.shareError"
    : "pillbox.shareError";

  useEffect(() => {
    if (!memberId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    const load = async () => {
      try {
        const blob = isShoppingList
          ? await downloadShoppingListPdf()
          : await downloadSharePdf(
              view,
              view === "schedule" ? date : undefined
            );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch {
        if (!cancelled) showToast({ variant: "error", message: t(errorKey) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      // Revoked on unmount, not after render — the preview keeps reading from
      // this URL for as long as the page is open.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, kind, view, date]);

  const handleShare = async () => {
    if (sharing || !pdfUrl) return;
    setSharing(true);
    try {
      // Desktop has no OS share sheet — save the already-fetched blob rather
      // than re-requesting it.
      if (isDesktop) {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = isShoppingList
          ? "pillbox-shopping-list.pdf"
          : `${memberDisplayName || "pillbox"}-pillbox.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      const handed = isShoppingList
        ? triggerNativePillboxShoppingListShare({
            memberName: memberDisplayName,
          })
        : triggerNativePillboxShare({
            memberId,
            memberName: memberDisplayName,
            view,
            date: view === "schedule" ? date : undefined,
          });
      if (handed) return;

      // Non-native browser fallback: open the PDF we already have.
      window.open(pdfUrl, "_blank");
    } catch {
      showToast({ variant: "error", message: t(errorKey) });
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="flex flex-col items-center w-full min-h-screen ">
      <div className="heading-h7-bold fixed z-[99999] bg-white w-full text-center pb-[40px]">
        {isShoppingList ? t("pillbox.shoppingList.title") : t("pillbox.title")}
      </div>
      <div className="w-full max-w-2xl mx-auto">
        {loading && <MLoader />}
        {pdfUrl && <PDFPreview fileUrl={pdfUrl} onLoadSuccess={setNoOfPages} />}
      </div>
      <div className="dashboardEmergencyShareContainer confirm-footer">
        <MButton
          variant="filled"
          size="md"
          className="w-full"
          onClick={handleShare}
          disabled={!pdfUrl || sharing}
          leftIcon={
            isDesktop ? <IconDownload size={16} /> : <IconShare3 size={16} />
          }
        >
          {isDesktop ? t("common.downloadPdf") : t("pillbox.share")}
        </MButton>
      </div>
    </div>
  );
};

export default PillboxPdfViewer;
